import React, { FC, useEffect, useMemo, useState } from "react";
import Modal from "@/components/Modal";
import Button from "@/components/Button";
import { useAppTheme } from "@/utils/theme";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import FormControlLabel from "@mui/material/FormControlLabel";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import dayjs from "dayjs";
import { useOrganizations } from "@/hooks/useOrganizations";
import { useCreateProgram, useUpdateProgram } from "@/hooks/usePrograms";
import { useUserStore } from "@/hooks/useUserStore";
import { useFeedBack } from "@/providers/FeedBackProvider/FeedBackContext";
import { ErrorResponse } from "@/domain/error.interface";
import {
  ContextualAgentFlowStatus,
  ProgramCheckInMethod,
  Programs,
} from "@/domain/programs.interface";
import { ProgramPayload } from "@/services/program.service";
import { OrganizationUnit } from "@/domain/organizations.interface";

type ProgramStatus = "PENDING" | "ACTIVE" | "COMPLETED" | "CANCELED";

type ContextualAgentFlowForm = {
  flowType: string;
  programDay: number | string;
  amount: number | string;
  status: ContextualAgentFlowStatus;
};

type ProgramFormData = {
  // General
  name: string;
  status: ProgramStatus;
  eventName: string;
  orgUnitIds: string[];
  // Parameters
  requiresCaseNumber: boolean;
  maxParticipants: number | string;
  budgetLimit: number | string;
  maxEarningsPerParticipant: number | string;
  startDate: string;
  endDate: string;
  // Configuration - Modules
  enabledJournalsModule: boolean;
  enabledJournalsBackofficeView: boolean;
  enabledServiceProvidersDirectoryModule: boolean;
  viewBackofficeApptsEnabled: boolean;
  // Configuration - Rewards
  enabledRewardUponAccountCreation: boolean;
  enabledRewardUponCheckIn: boolean;
  enabledRewardUponApptCreation: boolean;
  enabledJournalsReward: boolean;
  potentialRewardsValue: number | string;
  // Configuration - Appointment notifications
  enabledPushApptEveningBefore: boolean;
  timePushApptEveningBefore: number | string;
  enabledPushApptMorningBefore: boolean;
  timePushApptMorningBefore: number | string;
  enabledPushApptBefore: boolean;
  minutesPushApptBefore: number | string;
  enabledPushApptCheckInBefore: boolean;
  minutesPushApptCheckInBefore: number | string;
  enabledPushApptMissedFirst: boolean;
  minutesPushApptMissedFirst: number | string;
  enabledPushApptMissedSecond: boolean;
  minutesPushApptMissedSecond: number | string;
  enabledPushApptMissedThird: boolean;
  minutesPushApptMissedThird: number | string;
  enabledPushApptPostCheckInFirst: boolean;
  minutesPushApptPostCheckInFirst: number | string;
  enabledPushApptPostCheckInSecond: boolean;
  minutesPushApptPostCheckInSecond: number | string;
  enabledPushApptPostCheckInThird: boolean;
  minutesPushApptPostCheckInThird: number | string;
  enabledPushApptPostCheckInFourth: boolean;
  minutesPushApptPostCheckInFourth: number | string;
  enabledMissedAppointmentReason: boolean;
  enabledRewardMissedApptReason: boolean;
  enabledViewAppointmentMap: boolean;
  onboardPhoneNumber: string;
  participantUserGuideURL: string;
  weeklyMissedRescheduleReminderEnabled: boolean;
  postCheckInRescheduleReminderEnabled: boolean;
  weeklyPostCheckinScheduleReminderEnabled: boolean;
  // Configuration - Contextual agent
  enabledContextualAgent: boolean;
  enabledRewardCAFlow: boolean;
  agentFlowNotOnWeekends: boolean;
  contextualAgentFlows: ContextualAgentFlowForm[];
  // Configuration - Other
  onboardReleasedDate: string;
  programDays: number | string;
  specifyAppointmentReason: string;
  checkInMethod: ProgramCheckInMethod | "";
};

const PROGRAM_STATUSES: ProgramStatus[] = [
  "PENDING",
  "ACTIVE",
  "COMPLETED",
  "CANCELED",
];

const CHECK_IN_METHODS: ProgramCheckInMethod[] = [
  "LOCATION_ONLY",
  "QR_ONLY",
  "LOCATION_QR",
];

const optionalNumber = () =>
  yup
    .number()
    .transform((value, originalValue) => {
      if (
        originalValue === "" ||
        originalValue === null ||
        originalValue === undefined
      ) {
        return undefined;
      }
      return typeof value === "number" && !isNaN(value) ? value : undefined;
    })
    .nullable()
    .optional();

const buildSchema = () =>
  yup.object().shape({
    // General
    name: yup.string().trim().required("Name is required"),
    status: yup
      .mixed<ProgramStatus>()
      .oneOf(PROGRAM_STATUSES)
      .required("Status is required"),
    eventName: yup.string().trim().optional(),
    orgUnitIds: yup
      .array()
      .of(yup.string().required())
      .min(1, "At least one organization is required")
      .required("At least one organization is required"),
    // Parameters
    requiresCaseNumber: yup.boolean().required(),
    maxParticipants: optionalNumber().min(0, "Must be 0 or greater"),
    budgetLimit: optionalNumber().min(0, "Must be 0 or greater"),
    maxEarningsPerParticipant: optionalNumber().min(0, "Must be 0 or greater"),
    startDate: yup.string().optional(),
    endDate: yup.string().optional(),
    // Modules
    enabledJournalsModule: yup.boolean().required(),
    enabledJournalsBackofficeView: yup.boolean().required(),
    enabledServiceProvidersDirectoryModule: yup.boolean().required(),
    viewBackofficeApptsEnabled: yup.boolean().required(),
    // Rewards
    enabledRewardUponAccountCreation: yup.boolean().required(),
    enabledRewardUponCheckIn: yup.boolean().required(),
    enabledRewardUponApptCreation: yup.boolean().required(),
    enabledJournalsReward: yup.boolean().required(),
    potentialRewardsValue: optionalNumber().min(0, "Must be 0 or greater"),
    // Notifications
    enabledPushApptEveningBefore: yup.boolean().required(),
    timePushApptEveningBefore: optionalNumber().min(0, "Must be 0 or greater"),
    enabledPushApptMorningBefore: yup.boolean().required(),
    timePushApptMorningBefore: optionalNumber().min(0, "Must be 0 or greater"),
    enabledPushApptBefore: yup.boolean().required(),
    minutesPushApptBefore: optionalNumber().min(0, "Must be 0 or greater"),
    enabledPushApptCheckInBefore: yup.boolean().required(),
    minutesPushApptCheckInBefore: optionalNumber().min(
      0,
      "Must be 0 or greater"
    ),
    enabledPushApptMissedFirst: yup.boolean().required(),
    minutesPushApptMissedFirst: optionalNumber().min(0, "Must be 0 or greater"),
    enabledPushApptMissedSecond: yup.boolean().required(),
    minutesPushApptMissedSecond: optionalNumber().min(
      0,
      "Must be 0 or greater"
    ),
    enabledPushApptMissedThird: yup.boolean().required(),
    minutesPushApptMissedThird: optionalNumber().min(0, "Must be 0 or greater"),
    enabledPushApptPostCheckInFirst: yup.boolean().required(),
    minutesPushApptPostCheckInFirst: optionalNumber().min(
      0,
      "Must be 0 or greater"
    ),
    enabledPushApptPostCheckInSecond: yup.boolean().required(),
    minutesPushApptPostCheckInSecond: optionalNumber().min(
      0,
      "Must be 0 or greater"
    ),
    enabledPushApptPostCheckInThird: yup.boolean().required(),
    minutesPushApptPostCheckInThird: optionalNumber().min(
      0,
      "Must be 0 or greater"
    ),
    enabledPushApptPostCheckInFourth: yup.boolean().required(),
    minutesPushApptPostCheckInFourth: optionalNumber().min(
      0,
      "Must be 0 or greater"
    ),
    enabledMissedAppointmentReason: yup.boolean().required(),
    enabledRewardMissedApptReason: yup.boolean().required(),
    enabledViewAppointmentMap: yup.boolean().required(),
    onboardPhoneNumber: yup.string().trim().optional(),
    participantUserGuideURL: yup.string().trim().optional(),
    weeklyMissedRescheduleReminderEnabled: yup.boolean().required(),
    postCheckInRescheduleReminderEnabled: yup.boolean().required(),
    weeklyPostCheckinScheduleReminderEnabled: yup.boolean().required(),
    // Contextual agent
    enabledContextualAgent: yup.boolean().required(),
    enabledRewardCAFlow: yup.boolean().required(),
    agentFlowNotOnWeekends: yup.boolean().required(),
    contextualAgentFlows: yup
      .array()
      .of(
        yup.object().shape({
          flowType: yup.string().trim().required("Flow type is required"),
          programDay: yup
            .number()
            .typeError("Must be a number")
            .required("Program day is required")
            .min(0, "Must be 0 or greater"),
          amount: yup
            .number()
            .typeError("Must be a number")
            .required("Amount is required")
            .min(0, "Must be 0 or greater"),
          status: yup
            .mixed<ContextualAgentFlowStatus>()
            .oneOf(["ACTIVE", "INACTIVE"])
            .required("Status is required"),
        })
      )
      .default([]),
    // Other
    onboardReleasedDate: yup
      .string()
      .oneOf(["", "readonly", "writing"])
      .optional(),
    programDays: optionalNumber().min(0, "Must be 0 or greater"),
    specifyAppointmentReason: yup.string().trim().optional(),
    checkInMethod: yup
      .mixed<ProgramCheckInMethod | "">()
      .oneOf(["", ...CHECK_IN_METHODS])
      .optional(),
  });

const DEFAULT_VALUES: ProgramFormData = {
  name: "",
  status: "ACTIVE",
  eventName: "",
  orgUnitIds: [],
  requiresCaseNumber: false,
  maxParticipants: 0,
  budgetLimit: 0,
  maxEarningsPerParticipant: 0,
  startDate: "",
  endDate: "",
  enabledJournalsModule: false,
  enabledJournalsBackofficeView: false,
  enabledServiceProvidersDirectoryModule: false,
  viewBackofficeApptsEnabled: false,
  enabledRewardUponAccountCreation: false,
  enabledRewardUponCheckIn: false,
  enabledRewardUponApptCreation: false,
  enabledJournalsReward: false,
  potentialRewardsValue: 0,
  enabledPushApptEveningBefore: false,
  timePushApptEveningBefore: 0,
  enabledPushApptMorningBefore: false,
  timePushApptMorningBefore: 0,
  enabledPushApptBefore: false,
  minutesPushApptBefore: 0,
  enabledPushApptCheckInBefore: false,
  minutesPushApptCheckInBefore: 0,
  enabledPushApptMissedFirst: false,
  minutesPushApptMissedFirst: 0,
  enabledPushApptMissedSecond: false,
  minutesPushApptMissedSecond: 0,
  enabledPushApptMissedThird: false,
  minutesPushApptMissedThird: 0,
  enabledPushApptPostCheckInFirst: false,
  minutesPushApptPostCheckInFirst: 0,
  enabledPushApptPostCheckInSecond: false,
  minutesPushApptPostCheckInSecond: 0,
  enabledPushApptPostCheckInThird: false,
  minutesPushApptPostCheckInThird: 0,
  enabledPushApptPostCheckInFourth: false,
  minutesPushApptPostCheckInFourth: 0,
  enabledMissedAppointmentReason: false,
  enabledRewardMissedApptReason: false,
  enabledViewAppointmentMap: false,
  onboardPhoneNumber: "",
  participantUserGuideURL: "",
  weeklyMissedRescheduleReminderEnabled: false,
  postCheckInRescheduleReminderEnabled: false,
  weeklyPostCheckinScheduleReminderEnabled: false,
  enabledContextualAgent: false,
  enabledRewardCAFlow: false,
  agentFlowNotOnWeekends: false,
  contextualAgentFlows: [],
  onboardReleasedDate: "",
  programDays: 0,
  specifyAppointmentReason: "",
  checkInMethod: "",
};

type ProgramModalProps = {
  open: boolean;
  onClose: () => void;
  program?: Programs | null;
};

const toDateInputValue = (value?: string | Date): string => {
  if (!value) return "";
  const d = dayjs(value);
  return d.isValid() ? d.format("YYYY-MM-DD") : "";
};

const toIsoDate = (value?: string): string | undefined => {
  if (!value) return undefined;
  const d = dayjs(value);
  return d.isValid() ? d.toISOString() : undefined;
};

const coerceNumber = (
  value: number | string | undefined
): number | undefined => {
  if (value === "" || value === null || value === undefined) return undefined;
  const n = typeof value === "number" ? value : Number(value);
  return isNaN(n) ? undefined : n;
};

const hasValue = (v: unknown): boolean => {
  if (v === undefined || v === null) return false;
  if (typeof v === "string") return v.trim().length > 0;
  if (Array.isArray(v)) return v.length > 0;
  if (typeof v === "boolean") return v === true;
  if (typeof v === "number") return !isNaN(v);
  return true;
};

type SectionTitleProps = { title: string };
const SectionTitle: FC<SectionTitleProps> = ({ title }) => (
  <Box sx={{ width: "100%", mt: 1, mb: 0.5 }}>
    <Typography variant="titleSmall" component="div">
      {title}
    </Typography>
    <Divider sx={{ mt: 1 }} />
  </Box>
);

type TabPanelProps = {
  children?: React.ReactNode;
  index: number;
  value: number;
};

const TabPanel: FC<TabPanelProps> = ({ children, index, value }) => {
  if (value !== index) return null;
  return (
    <Box role="tabpanel" sx={{ pt: 2 }}>
      {children}
    </Box>
  );
};

const ProgramModal: FC<ProgramModalProps> = ({ open, onClose, program }) => {
  const theme = useAppTheme();
  const { showSnackBar } = useFeedBack();
  const isEditMode = !!program?._id;

  const [activeTab, setActiveTab] = useState(0);

  const createProgram = useCreateProgram();
  const updateProgram = useUpdateProgram();
  const isPending = createProgram.isPending || updateProgram.isPending;

  const { userInfo } = useUserStore();
  const currentUserId = userInfo?._id;

  const { organizations, isLoadingOrganizations } = useOrganizations({
    status: "ACTIVE",
    pageNumber: 0,
    pageSize: 500,
    sortField: "name",
    sortOrder: 1,
  });

  const availableOrganizations: OrganizationUnit[] = useMemo(
    () => organizations?.list || [],
    [organizations]
  );

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<ProgramFormData>({
    resolver: yupResolver(buildSchema()) as never,
    mode: "all",
    defaultValues: DEFAULT_VALUES,
  });

  const {
    fields: flowFields,
    append: appendFlow,
    remove: removeFlow,
  } = useFieldArray({
    control,
    name: "contextualAgentFlows",
  });

  useEffect(() => {
    if (!open) return;
    setActiveTab(0);
    if (program) {
      const p = program.parameters || {};
      const c = program.configuration || {};
      reset({
        name: program.name || "",
        status: (program.status as ProgramStatus) || "ACTIVE",
        eventName: program.eventName || "",
        orgUnitIds: Array.isArray(program.orgUnitIds)
          ? program.orgUnitIds
          : Array.isArray(program.orgUnitId)
            ? program.orgUnitId
            : [],
        requiresCaseNumber: !!p.requiresCaseNumber,
        maxParticipants: p.maxParticipants ?? 0,
        budgetLimit: p.budgetLimit ?? 0,
        maxEarningsPerParticipant: p.maxEarningsPerParticipant ?? 0,
        startDate: toDateInputValue(p.startDate),
        endDate: toDateInputValue(p.endDate),
        enabledJournalsModule: !!c.enabledJournalsModule,
        enabledJournalsBackofficeView: !!c.enabledJournalsBackofficeView,
        enabledServiceProvidersDirectoryModule:
          !!c.enabledServiceProvidersDirectoryModule,
        viewBackofficeApptsEnabled: !!c.viewBackofficeApptsEnabled,
        enabledRewardUponAccountCreation: !!c.enabledRewardUponAccountCreation,
        enabledRewardUponCheckIn: !!c.enabledRewardUponCheckIn,
        enabledRewardUponApptCreation: !!c.enabledRewardUponApptCreation,
        enabledJournalsReward: !!c.enabledJournalsReward,
        potentialRewardsValue: c.potentialRewardsValue ?? 0,
        enabledPushApptEveningBefore: !!c.enabledPushApptEveningBefore,
        timePushApptEveningBefore: c.timePushApptEveningBefore ?? 0,
        enabledPushApptMorningBefore: !!c.enabledPushApptMorningBefore,
        timePushApptMorningBefore: c.timePushApptMorningBefore ?? 0,
        enabledPushApptBefore: !!c.enabledPushApptBefore,
        minutesPushApptBefore: c.minutesPushApptBefore ?? 0,
        enabledPushApptCheckInBefore: !!c.enabledPushApptCheckInBefore,
        minutesPushApptCheckInBefore: c.minutesPushApptCheckInBefore ?? 0,
        enabledPushApptMissedFirst: !!c.enabledPushApptMissedFirst,
        minutesPushApptMissedFirst: c.minutesPushApptMissedFirst ?? 0,
        enabledPushApptMissedSecond: !!c.enabledPushApptMissedSecond,
        minutesPushApptMissedSecond: c.minutesPushApptMissedSecond ?? 0,
        enabledPushApptMissedThird: !!c.enabledPushApptMissedThird,
        minutesPushApptMissedThird: c.minutesPushApptMissedThird ?? 0,
        enabledPushApptPostCheckInFirst: !!c.enabledPushApptPostCheckInFirst,
        minutesPushApptPostCheckInFirst: c.minutesPushApptPostCheckInFirst ?? 0,
        enabledPushApptPostCheckInSecond: !!c.enabledPushApptPostCheckInSecond,
        minutesPushApptPostCheckInSecond:
          c.minutesPushApptPostCheckInSecond ?? 0,
        enabledPushApptPostCheckInThird: !!c.enabledPushApptPostCheckInThird,
        minutesPushApptPostCheckInThird: c.minutesPushApptPostCheckInThird ?? 0,
        enabledPushApptPostCheckInFourth: !!c.enabledPushApptPostCheckInFourth,
        minutesPushApptPostCheckInFourth:
          c.minutesPushApptPostCheckInFourth ?? 0,
        enabledMissedAppointmentReason: !!c.enabledMissedAppointmentReason,
        enabledRewardMissedApptReason: !!c.enabledRewardMissedApptReason,
        enabledViewAppointmentMap: !!c.enabledViewAppointmentMap,
        onboardPhoneNumber: c.onboardPhoneNumber ?? "",
        participantUserGuideURL: c.participantUserGuideURL ?? "",
        weeklyMissedRescheduleReminderEnabled:
          !!c.weeklyMissedRescheduleReminderEnabled,
        postCheckInRescheduleReminderEnabled:
          !!c.postCheckInRescheduleReminderEnabled,
        weeklyPostCheckinScheduleReminderEnabled:
          !!c.weeklyPostCheckinScheduleReminderEnabled,
        enabledContextualAgent: !!c.enabledContextualAgent,
        enabledRewardCAFlow: !!c.enabledRewardCAFlow,
        agentFlowNotOnWeekends: !!c.agentFlowNotOnWeekends,
        contextualAgentFlows: Array.isArray(c.contextualAgentFlows)
          ? c.contextualAgentFlows.map((f) => ({
              flowType: f.flowType ?? "",
              programDay: f.programDay ?? 0,
              amount: f.amount ?? 0,
              status: (f.status as ContextualAgentFlowStatus) ?? "ACTIVE",
            }))
          : [],
        onboardReleasedDate: c.onboardReleasedDate ?? "",
        programDays: c.programDays ?? 0,
        specifyAppointmentReason: c.specifyAppointmentReason ?? "",
        checkInMethod: (c.checkInMethod as ProgramCheckInMethod) ?? "",
      });
    } else {
      reset(DEFAULT_VALUES);
    }
  }, [program, open, reset]);

  const buildPayload = (data: ProgramFormData): ProgramPayload => {
    const parameters = {
      requiresCaseNumber: data.requiresCaseNumber,
      maxParticipants: coerceNumber(data.maxParticipants),
      budgetLimit: coerceNumber(data.budgetLimit),
      maxEarningsPerParticipant: coerceNumber(data.maxEarningsPerParticipant),
      startDate: toIsoDate(data.startDate),
      endDate: toIsoDate(data.endDate),
    };

    const configuration = {
      enabledJournalsModule: data.enabledJournalsModule,
      enabledJournalsBackofficeView: data.enabledJournalsBackofficeView,
      enabledServiceProvidersDirectoryModule:
        data.enabledServiceProvidersDirectoryModule,
      viewBackofficeApptsEnabled: data.viewBackofficeApptsEnabled,
      enabledRewardUponAccountCreation: data.enabledRewardUponAccountCreation,
      enabledRewardUponCheckIn: data.enabledRewardUponCheckIn,
      enabledRewardUponApptCreation: data.enabledRewardUponApptCreation,
      enabledJournalsReward: data.enabledJournalsReward,
      potentialRewardsValue: coerceNumber(data.potentialRewardsValue),
      enabledPushApptEveningBefore: data.enabledPushApptEveningBefore,
      timePushApptEveningBefore: coerceNumber(data.timePushApptEveningBefore),
      enabledPushApptMorningBefore: data.enabledPushApptMorningBefore,
      timePushApptMorningBefore: coerceNumber(data.timePushApptMorningBefore),
      enabledPushApptBefore: data.enabledPushApptBefore,
      minutesPushApptBefore: coerceNumber(data.minutesPushApptBefore),
      enabledPushApptCheckInBefore: data.enabledPushApptCheckInBefore,
      minutesPushApptCheckInBefore: coerceNumber(
        data.minutesPushApptCheckInBefore
      ),
      enabledPushApptMissedFirst: data.enabledPushApptMissedFirst,
      minutesPushApptMissedFirst: coerceNumber(data.minutesPushApptMissedFirst),
      enabledPushApptMissedSecond: data.enabledPushApptMissedSecond,
      minutesPushApptMissedSecond: coerceNumber(
        data.minutesPushApptMissedSecond
      ),
      enabledPushApptMissedThird: data.enabledPushApptMissedThird,
      minutesPushApptMissedThird: coerceNumber(data.minutesPushApptMissedThird),
      enabledPushApptPostCheckInFirst: data.enabledPushApptPostCheckInFirst,
      minutesPushApptPostCheckInFirst: coerceNumber(
        data.minutesPushApptPostCheckInFirst
      ),
      enabledPushApptPostCheckInSecond: data.enabledPushApptPostCheckInSecond,
      minutesPushApptPostCheckInSecond: coerceNumber(
        data.minutesPushApptPostCheckInSecond
      ),
      enabledPushApptPostCheckInThird: data.enabledPushApptPostCheckInThird,
      minutesPushApptPostCheckInThird: coerceNumber(
        data.minutesPushApptPostCheckInThird
      ),
      enabledPushApptPostCheckInFourth: data.enabledPushApptPostCheckInFourth,
      minutesPushApptPostCheckInFourth: coerceNumber(
        data.minutesPushApptPostCheckInFourth
      ),
      enabledMissedAppointmentReason: data.enabledMissedAppointmentReason,
      enabledRewardMissedApptReason: data.enabledRewardMissedApptReason,
      enabledViewAppointmentMap: data.enabledViewAppointmentMap,
      onboardPhoneNumber: data.onboardPhoneNumber?.trim() || undefined,
      participantUserGuideURL:
        data.participantUserGuideURL?.trim() || undefined,
      weeklyMissedRescheduleReminderEnabled:
        data.weeklyMissedRescheduleReminderEnabled,
      postCheckInRescheduleReminderEnabled:
        data.postCheckInRescheduleReminderEnabled,
      weeklyPostCheckinScheduleReminderEnabled:
        data.weeklyPostCheckinScheduleReminderEnabled,
      enabledContextualAgent: data.enabledContextualAgent,
      enabledRewardCAFlow: data.enabledRewardCAFlow,
      agentFlowNotOnWeekends: data.agentFlowNotOnWeekends,
      contextualAgentFlows: (data.contextualAgentFlows || []).map((f) => ({
        flowType: f.flowType.trim(),
        programDay: Number(f.programDay) || 0,
        amount: Number(f.amount) || 0,
        status: f.status,
      })),
      onboardReleasedDate: data.onboardReleasedDate || undefined,
      programDays: coerceNumber(data.programDays),
      specifyAppointmentReason:
        data.specifyAppointmentReason?.trim() || undefined,
      checkInMethod: data.checkInMethod || undefined,
    };

    const hasAnyParameter = Object.values(parameters).some((v) => hasValue(v));
    const hasAnyConfiguration = Object.values(configuration).some((v) =>
      hasValue(v)
    );

    const payload: ProgramPayload = {
      name: data.name.trim(),
      status: data.status,
      orgUnitIds: data.orgUnitIds,
    };

    if (data.eventName?.trim()) {
      payload.eventName = data.eventName.trim();
    }

    if (hasAnyParameter) {
      payload.parameters = parameters;
    }

    if (hasAnyConfiguration) {
      payload.configuration = configuration;
    }

    return payload;
  };

  const onSubmit = async (data: ProgramFormData) => {
    const payload = buildPayload(data);
    try {
      if (isEditMode && program?._id) {
        await updateProgram.mutateAsync({
          id: program._id,
          data: { ...payload, updatedBy: currentUserId },
        });
        showSnackBar("The program has been updated.", "success");
      } else {
        await createProgram.mutateAsync({
          ...payload,
          createdBy: currentUserId,
        });
        showSnackBar("The program has been created.", "success");
      }
      onClose();
    } catch (error: unknown) {
      const err = error as ErrorResponse;
      showSnackBar(
        (Array.isArray(err.message) ? err.message[0] : err.message) ||
          err.error ||
          "An error occurred",
        "error"
      );
    }
  };

  const selectedOrgUnitIds = watch("orgUnitIds") || [];

  // Watchers for dependent "enabled" flags
  const wEveningBefore = watch("enabledPushApptEveningBefore");
  const wMorningBefore = watch("enabledPushApptMorningBefore");
  const wApptBefore = watch("enabledPushApptBefore");
  const wCheckInBefore = watch("enabledPushApptCheckInBefore");
  const wMissedFirst = watch("enabledPushApptMissedFirst");
  const wMissedSecond = watch("enabledPushApptMissedSecond");
  const wMissedThird = watch("enabledPushApptMissedThird");
  const wPostCheckInFirst = watch("enabledPushApptPostCheckInFirst");
  const wPostCheckInSecond = watch("enabledPushApptPostCheckInSecond");
  const wPostCheckInThird = watch("enabledPushApptPostCheckInThird");
  const wPostCheckInFourth = watch("enabledPushApptPostCheckInFourth");

  const renderBoolean = (
    name: keyof ProgramFormData,
    label: string,
    gridProps: { xs: number; sm?: number; md?: number } = { xs: 12, sm: 6 }
  ) => (
    <Grid item {...gridProps}>
      <Controller
        name={name}
        control={control}
        render={({ field }) => (
          <FormControlLabel
            control={
              <Switch
                checked={!!field.value}
                onChange={(_e, checked) => field.onChange(checked)}
                disabled={isPending}
              />
            }
            label={label}
          />
        )}
      />
    </Grid>
  );

  const renderNumber = (
    name: keyof ProgramFormData,
    label: string,
    disabled = false,
    gridProps: { xs: number; sm?: number; md?: number } = { xs: 12, sm: 6 }
  ) => (
    <Grid item {...gridProps}>
      <Controller
        name={name}
        control={control}
        render={({ field, fieldState }) => (
          <TextField
            {...field}
            value={field.value ?? ""}
            onChange={(e) => field.onChange(e.target.value)}
            type="number"
            label={label}
            fullWidth
            disabled={isPending || disabled}
            error={!!fieldState.error}
            helperText={fieldState.error?.message || ""}
          />
        )}
      />
    </Grid>
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      isPending={isPending}
      theme={theme}
      title={isEditMode ? "Edit Program" : "Create Program"}
      size={{
        width: "100%",
        minHeight: 640,
        maxHeight: 820,
        maxWidth: 880,
      }}
      content={
        <Box sx={{ width: "100%" }}>
          <Tabs
            value={activeTab}
            onChange={(_e, v) => setActiveTab(v)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ borderBottom: 1, borderColor: "divider" }}
          >
            <Tab label="General" />
            <Tab label="Parameters" />
            <Tab label="Configuration" />
          </Tabs>

          <TabPanel value={activeTab} index={0}>
            <Grid container spacing={"20px"}>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="name"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      value={field.value || ""}
                      onChange={(e) => field.onChange(e.target.value)}
                      label="Name"
                      fullWidth
                      required
                      error={!!errors.name}
                      helperText={errors.name?.message || ""}
                      disabled={isPending}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="status"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      select
                      label="Status"
                      fullWidth
                      required
                      error={!!errors.status}
                      helperText={errors.status?.message || ""}
                      disabled={isPending}
                    >
                      {PROGRAM_STATUSES.map((s) => (
                        <MenuItem key={s} value={s}>
                          {s}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="eventName"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      value={field.value || ""}
                      onChange={(e) => field.onChange(e.target.value)}
                      label="Event Name"
                      fullWidth
                      error={!!errors.eventName}
                      helperText={errors.eventName?.message || ""}
                      disabled={isPending}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Autocomplete
                  multiple
                  options={availableOrganizations}
                  loading={isLoadingOrganizations}
                  disabled={isPending}
                  getOptionLabel={(option) => option.name || option._id || ""}
                  isOptionEqualToValue={(option, value) =>
                    option._id === value._id
                  }
                  value={availableOrganizations.filter((o) =>
                    selectedOrgUnitIds.includes(o._id)
                  )}
                  onChange={(_e, selected) => {
                    setValue(
                      "orgUnitIds",
                      selected.map((o) => o._id),
                      { shouldDirty: true, shouldValidate: true }
                    );
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Organizations"
                      placeholder="Select organizations"
                      required
                      error={!!errors.orgUnitIds}
                      helperText={
                        (errors.orgUnitIds as { message?: string })?.message ||
                        ""
                      }
                    />
                  )}
                />
              </Grid>
            </Grid>
          </TabPanel>

          <TabPanel value={activeTab} index={1}>
            <Grid container spacing={"20px"}>
              {renderBoolean("requiresCaseNumber", "Requires case number", {
                xs: 12,
                sm: 12,
              })}
              {renderNumber("maxParticipants", "Max participants")}
              {renderNumber("budgetLimit", "Budget limit")}
              {renderNumber(
                "maxEarningsPerParticipant",
                "Max earnings per participant"
              )}
              <Grid item xs={12} sm={6}>
                <Controller
                  name="startDate"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      value={field.value || ""}
                      onChange={(e) => field.onChange(e.target.value)}
                      type="date"
                      label="Start date"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      disabled={isPending}
                      error={!!errors.startDate}
                      helperText={errors.startDate?.message || ""}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="endDate"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      value={field.value || ""}
                      onChange={(e) => field.onChange(e.target.value)}
                      type="date"
                      label="End date"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      disabled={isPending}
                      error={!!errors.endDate}
                      helperText={errors.endDate?.message || ""}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </TabPanel>

          <TabPanel value={activeTab} index={2}>
            <Grid container spacing={"16px"}>
              <SectionTitle title="Modules" />
              {renderBoolean("enabledJournalsModule", "Journals module")}
              {renderBoolean(
                "enabledJournalsBackofficeView",
                "Journals backoffice view"
              )}
              {renderBoolean(
                "enabledServiceProvidersDirectoryModule",
                "Service providers directory module"
              )}
              {renderBoolean(
                "viewBackofficeApptsEnabled",
                "View backoffice appointments"
              )}
              {renderBoolean(
                "enabledViewAppointmentMap",
                "Show appointment location on map"
              )}

              <SectionTitle title="Rewards" />
              {renderBoolean(
                "enabledRewardUponAccountCreation",
                "Reward upon account creation"
              )}
              {renderBoolean(
                "enabledRewardUponCheckIn",
                "Reward upon check-in"
              )}
              {renderBoolean(
                "enabledRewardUponApptCreation",
                "Reward upon appointment creation"
              )}
              {renderBoolean("enabledJournalsReward", "Journals reward")}
              {renderBoolean(
                "enabledRewardMissedApptReason",
                "Reward on missed appointment reason"
              )}
              {renderNumber("potentialRewardsValue", "Potential rewards value")}

              <SectionTitle title="Appointment notifications" />
              {renderBoolean(
                "enabledPushApptEveningBefore",
                "Push evening before"
              )}
              {renderNumber(
                "timePushApptEveningBefore",
                "Time (evening before)",
                !wEveningBefore
              )}
              {renderBoolean(
                "enabledPushApptMorningBefore",
                "Push morning before"
              )}
              {renderNumber(
                "timePushApptMorningBefore",
                "Time (morning before)",
                !wMorningBefore
              )}
              {renderBoolean(
                "enabledPushApptBefore",
                "Push before appointment"
              )}
              {renderNumber(
                "minutesPushApptBefore",
                "Minutes before",
                !wApptBefore
              )}
              {renderBoolean(
                "enabledPushApptCheckInBefore",
                "Push check-in before"
              )}
              {renderNumber(
                "minutesPushApptCheckInBefore",
                "Minutes (check-in before)",
                !wCheckInBefore
              )}
              {renderBoolean("enabledPushApptMissedFirst", "Push missed (1st)")}
              {renderNumber(
                "minutesPushApptMissedFirst",
                "Minutes (1st missed)",
                !wMissedFirst
              )}
              {renderBoolean(
                "enabledPushApptMissedSecond",
                "Push missed (2nd)"
              )}
              {renderNumber(
                "minutesPushApptMissedSecond",
                "Minutes (2nd missed)",
                !wMissedSecond
              )}
              {renderBoolean("enabledPushApptMissedThird", "Push missed (3rd)")}
              {renderNumber(
                "minutesPushApptMissedThird",
                "Minutes (3rd missed)",
                !wMissedThird
              )}
              {renderBoolean(
                "enabledPushApptPostCheckInFirst",
                "Push post check-in (1st)"
              )}
              {renderNumber(
                "minutesPushApptPostCheckInFirst",
                "Minutes (1st post check-in)",
                !wPostCheckInFirst
              )}
              {renderBoolean(
                "enabledPushApptPostCheckInSecond",
                "Push post check-in (2nd)"
              )}
              {renderNumber(
                "minutesPushApptPostCheckInSecond",
                "Minutes (2nd post check-in)",
                !wPostCheckInSecond
              )}
              {renderBoolean(
                "enabledPushApptPostCheckInThird",
                "Push post check-in (3rd)"
              )}
              {renderNumber(
                "minutesPushApptPostCheckInThird",
                "Minutes (3rd post check-in)",
                !wPostCheckInThird
              )}
              {renderBoolean(
                "enabledPushApptPostCheckInFourth",
                "Push post check-in (4th)"
              )}
              {renderNumber(
                "minutesPushApptPostCheckInFourth",
                "Minutes (4th post check-in)",
                !wPostCheckInFourth
              )}
              {renderBoolean(
                "enabledMissedAppointmentReason",
                "Ask reason for missed appointment"
              )}
              {renderBoolean(
                "weeklyMissedRescheduleReminderEnabled",
                "Weekly missed reschedule reminder"
              )}
              {renderBoolean(
                "postCheckInRescheduleReminderEnabled",
                "Post check-in reschedule reminder"
              )}
              {renderBoolean(
                "weeklyPostCheckinScheduleReminderEnabled",
                "Weekly post check-in schedule reminder"
              )}

              <SectionTitle title="Contextual agent" />
              {renderBoolean(
                "enabledContextualAgent",
                "Enable contextual agent"
              )}
              {renderBoolean("enabledRewardCAFlow", "Reward CA flow")}
              {renderBoolean(
                "agentFlowNotOnWeekends",
                "Agent flow not on weekends"
              )}

              <Grid item xs={12}>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ mb: 1 }}
                >
                  <Typography variant="labelLarge" component="div">
                    Contextual agent flows
                  </Typography>
                  <Button
                    variantType="save"
                    label="Add flow"
                    onClick={() =>
                      appendFlow({
                        flowType: "",
                        programDay: 0,
                        amount: 0,
                        status: "ACTIVE",
                      })
                    }
                    disabled={isPending}
                  />
                </Stack>
                <Stack spacing={2}>
                  {flowFields.length === 0 && (
                    <Typography variant="bodyMedium" color="text.secondary">
                      No flows configured.
                    </Typography>
                  )}
                  {flowFields.map((f, idx) => (
                    <Box
                      key={f.id}
                      sx={{
                        p: 2,
                        border: "1px solid",
                        borderColor: theme.palette.outline.main,
                        borderRadius: "8px",
                      }}
                    >
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={4}>
                          <Controller
                            name={`contextualAgentFlows.${idx}.flowType`}
                            control={control}
                            render={({ field, fieldState }) => (
                              <TextField
                                {...field}
                                value={field.value || ""}
                                onChange={(e) => field.onChange(e.target.value)}
                                label="Flow type"
                                fullWidth
                                required
                                disabled={isPending}
                                error={!!fieldState.error}
                                helperText={fieldState.error?.message || ""}
                              />
                            )}
                          />
                        </Grid>
                        <Grid item xs={6} sm={2}>
                          <Controller
                            name={`contextualAgentFlows.${idx}.programDay`}
                            control={control}
                            render={({ field, fieldState }) => (
                              <TextField
                                {...field}
                                value={field.value ?? ""}
                                onChange={(e) => field.onChange(e.target.value)}
                                type="number"
                                label="Program day"
                                fullWidth
                                required
                                disabled={isPending}
                                error={!!fieldState.error}
                                helperText={fieldState.error?.message || ""}
                              />
                            )}
                          />
                        </Grid>
                        <Grid item xs={6} sm={2}>
                          <Controller
                            name={`contextualAgentFlows.${idx}.amount`}
                            control={control}
                            render={({ field, fieldState }) => (
                              <TextField
                                {...field}
                                value={field.value ?? ""}
                                onChange={(e) => field.onChange(e.target.value)}
                                type="number"
                                label="Amount"
                                fullWidth
                                required
                                disabled={isPending}
                                error={!!fieldState.error}
                                helperText={fieldState.error?.message || ""}
                              />
                            )}
                          />
                        </Grid>
                        <Grid item xs={10} sm={3}>
                          <Controller
                            name={`contextualAgentFlows.${idx}.status`}
                            control={control}
                            render={({ field, fieldState }) => (
                              <TextField
                                {...field}
                                select
                                label="Status"
                                fullWidth
                                required
                                disabled={isPending}
                                error={!!fieldState.error}
                                helperText={fieldState.error?.message || ""}
                              >
                                <MenuItem value="ACTIVE">Active</MenuItem>
                                <MenuItem value="INACTIVE">Inactive</MenuItem>
                              </TextField>
                            )}
                          />
                        </Grid>
                        <Grid
                          item
                          xs={2}
                          sm={1}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <IconButton
                            aria-label="Remove flow"
                            onClick={() => removeFlow(idx)}
                            disabled={isPending}
                            sx={{ color: theme.palette.error.main }}
                          >
                            <DeleteOutlineIcon />
                          </IconButton>
                        </Grid>
                      </Grid>
                    </Box>
                  ))}
                </Stack>
              </Grid>

              <SectionTitle title="Other" />
              <Grid item xs={12} sm={6}>
                <Controller
                  name="onboardReleasedDate"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      select
                      value={field.value || ""}
                      onChange={(e) => field.onChange(e.target.value)}
                      label="Onboard released date mode"
                      fullWidth
                      disabled={isPending}
                    >
                      <MenuItem value="">None</MenuItem>
                      <MenuItem value="readonly">Read only</MenuItem>
                      <MenuItem value="writing">Writing</MenuItem>
                    </TextField>
                  )}
                />
              </Grid>
              {renderNumber("programDays", "Program days")}
              <Grid item xs={12} sm={6}>
                <Controller
                  name="specifyAppointmentReason"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      value={field.value || ""}
                      onChange={(e) => field.onChange(e.target.value)}
                      label="Specify appointment reason"
                      fullWidth
                      disabled={isPending}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="onboardPhoneNumber"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      value={field.value || ""}
                      onChange={(e) => field.onChange(e.target.value)}
                      label="Onboard phone number"
                      placeholder="+1 555-555-5555"
                      fullWidth
                      disabled={isPending}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="participantUserGuideURL"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      value={field.value || ""}
                      onChange={(e) => field.onChange(e.target.value)}
                      label="Participant user guide URL"
                      placeholder="https://..."
                      fullWidth
                      disabled={isPending}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="checkInMethod"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      value={field.value || ""}
                      onChange={(e) => field.onChange(e.target.value)}
                      select
                      label="Check-in method"
                      fullWidth
                      disabled={isPending}
                    >
                      <MenuItem value="">None</MenuItem>
                      {CHECK_IN_METHODS.map((m) => (
                        <MenuItem key={m} value={m}>
                          {m}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                />
              </Grid>
            </Grid>
          </TabPanel>
        </Box>
      }
      actions={
        <Box sx={{ display: "flex", gap: "10px" }}>
          <Button
            variantType="cancel"
            onClick={onClose}
            disabled={isPending}
            label="CANCEL"
          />
          <Button
            variantType="save"
            onClick={() => handleSubmit(onSubmit)()}
            disabled={isPending}
            isLoading={isPending}
            label="SAVE"
          />
        </Box>
      }
    />
  );
};

export default ProgramModal;
